/**
 * Formatting helpers. House style (ui-design §3.13): curly quotes, no em or en
 * dashes in UI copy, middle dots between clauses, no marketing vocabulary.
 *
 * The date, count and clause helpers live in `@ni/shared` (the report export
 * renders the same words on the server) and are re-exported here.
 */

import { formatDay } from '@ni/shared';

export {
  CLAUSE_SEPARATOR,
  formatCount,
  formatDate,
  formatDay,
  formatLoose,
  formatMonth,
  joinClauses,
  keepLines,
  plural,
} from '@ni/shared';

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/** "Tuesday, Aug 18, 2026" — the demo's home subline. */
export function formatWeekday(at: Date): string {
  return `${WEEKDAYS[at.getDay()] ?? ''}, ${formatDay(at.toISOString())}`;
}

/**
 * "2:14 PM" — an instant as a 12-hour American wall clock in the reader's own
 * zone. The run timeline is read within the hour it describes, so the day
 * would be noise on every row.
 */
export function formatClock(timestamp: string | null | undefined): string {
  if (!timestamp) return '';
  const at = new Date(timestamp);
  if (Number.isNaN(at.getTime())) return '';
  const hours = at.getHours();
  const hour = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour}:${String(at.getMinutes()).padStart(2, '0')} ${hours < 12 ? 'AM' : 'PM'}`;
}

/** "DV" from "Daniel Voss" — the record head's monogram. */
export function initials(name: string): string {
  const words = name
    .split(/\s+/)
    .map((word) => word.trim())
    .filter((word) => word.length > 0);
  if (words.length === 0) return '?';
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

/**
 * **How long something has been running**, as a clock: "0:42", "3:05", "12:30".
 *
 * The import chat is the one place this app counts *up*. A planner turn is
 * seconds to a couple of minutes and the office has no other way to tell a slow
 * turn from a lost one, so the line under the composer carries a number that
 * moves. Minutes and seconds, never "42s" and never "a minute ago": the reader
 * is timing something, not dating it.
 */
export function formatElapsed(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, '0')}`;
}

/** Sentence-case a snake_case key for the record block's labels. */
export function humanize(key: string): string {
  const words = key.replace(/[_-]+/g, ' ').trim();
  if (words.length === 0) return key;
  return words.charAt(0).toUpperCase() + words.slice(1);
}
